import React, { useState, useEffect, useRef } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  ShieldAlert, Plus, Minus, X, Square, Clock,
  Home, List, BarChart2, Settings, Lock, Unlock,
  FileText, RotateCcw, AlertTriangle, Share2, Check, Download, Power, Trophy, Trash2
} from 'lucide-react';
import MatchReport from './MatchReport';
import ShareReport from './ShareReport';
import StatsView from './StatsView';
import SettingsView from './SettingsView';
import { downloadReportPDF } from './pdfUtil';
import { AuthProvider, useAuth } from './tournament/AuthContext';
import { api as tournamentApi } from './tournament/api';
import Login from './tournament/Login';
import Tournaments from './tournament/Tournaments';
import TournamentDetail from './tournament/TournamentDetail';
import PublicPortal from './tournament/PublicPortal';
import PublicEvent from './tournament/PublicEvent';
import Checkin from './tournament/Checkin';
import Pricing from './tournament/Pricing';
import BillingReturn from './tournament/BillingReturn';

const DEFAULT_SETTINGS = {
  defaultPeriods: 2,
  defaultPeriodLength: 45,
  defaultSinBin: 10,
  confirmEndMatch: false,
  autoSecondYellow: true,
  quickGoal: false,
};

const loadSettings = () => {
  try {
    const raw = localStorage.getItem('refcontrol_settings');
    return raw ? { ...DEFAULT_SETTINGS, ...JSON.parse(raw) } : DEFAULT_SETTINGS;
  } catch {
    return DEFAULT_SETTINGS;
  }
};

const DEFAULT_LENGTH_PRESETS = [45, 40, 35, 30, 25, 20];

const loadLengthPresets = () => {
  try {
    const raw = localStorage.getItem('refcontrol_length_presets');
    const arr = raw ? JSON.parse(raw) : DEFAULT_LENGTH_PRESETS;
    return Array.isArray(arr) && arr.length ? arr : DEFAULT_LENGTH_PRESETS;
  } catch {
    return DEFAULT_LENGTH_PRESETS;
  }
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Per-user (logged in) or per-device owner key so saved matches stay private to this user
const getOwnerKey = () => {
  const tok = localStorage.getItem('rc_token');
  if (tok) {
    try {
      const sub = JSON.parse(atob(tok.split('.')[1])).sub;
      if (sub) return `u_${sub}`;
    } catch (e) { /* fall through to device key */ }
  }
  let k = localStorage.getItem('tournevo_owner_key');
  if (!k) {
    k = (window.crypto && window.crypto.randomUUID) ? `d_${window.crypto.randomUUID()}` : `d_${Date.now()}_${Math.random().toString(36).slice(2)}`;
    localStorage.setItem('tournevo_owner_key', k);
  }
  return k;
};

// --- UTILITY FUNCTIONS ---
const formatTime = (totalSeconds) => {
  const m = Math.floor(totalSeconds / 60).toString().padStart(2, '0');
  const s = (totalSeconds % 60).toString().padStart(2, '0');
  return `${m}:${s}`;
};

const CardIcon = ({ color, className = "w-6 h-8" }) => (
  <div className={`${className} rounded-sm shadow-md ${color === 'yellow' ? 'bg-[#FFD400]' : color === 'red' ? 'bg-[#FF3B30]' : 'bg-[#00D26A]'}`} />
);

// --- STABLE UI COMPONENTS ---
const Input = React.memo(({ label, value, onChange, placeholder, type = "text", testId }) => (
  <div>
    <label className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-2 block">{label}</label>
    <input
      data-testid={testId}
      type={type}
      value={value}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full bg-[#1C1C1E] border border-gray-800 rounded-xl p-4 text-white text-sm focus:outline-none focus:border-[#00BFFF] transition-colors"
    />
  </div>
));

const Stepper = React.memo(({ label, value, onChange, min, max, testId }) => (
  <div className="flex items-center justify-between">
    <label className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">{label}</label>
    <div className="flex items-center space-x-4 bg-[#1C1C1E] p-1.5 rounded-xl border border-gray-800">
      <button data-testid={testId ? `${testId}-minus` : undefined} onClick={() => onChange(Math.max(min, value - 1))} className="p-2 text-[#00BFFF] hover:bg-gray-800 rounded-lg"><Minus size={16} /></button>
      <span className="text-sm font-bold w-6 text-center">{value}</span>
      <button data-testid={testId ? `${testId}-plus` : undefined} onClick={() => onChange(Math.min(max, value + 1))} className="p-2 text-[#00BFFF] hover:bg-gray-800 rounded-lg"><Plus size={16} /></button>
    </div>
  </div>
));

const ActionBtn = React.memo(({ label, icon, onClick, testId }) => (
  <button data-testid={testId} onClick={onClick} className="bg-[#1C1C1E] border border-gray-800 rounded-xl py-3 flex flex-col items-center justify-center space-y-1 hover:bg-gray-800 active:scale-95 transition-all">
    <div className="h-6 flex items-center justify-center">{icon}</div>
    <span className="text-[9px] font-bold text-gray-300 tracking-wider">{label}</span>
  </button>
));

const StatCard = React.memo(({ label, value, testId }) => (
  <div data-testid={testId} className="bg-[#1C1C1E] border border-gray-800 rounded-2xl p-4 text-center">
    <p className="text-[10px] text-gray-500 uppercase font-bold tracking-widest mb-1">{label}</p>
    <p className="text-3xl font-black text-white">{value}</p>
  </div>
));

const NavBtn = React.memo(({ icon, label, isActive, onClick, testId }) => (
  <button
    data-testid={testId}
    onClick={onClick}
    className={`flex flex-col items-center space-y-1 ${isActive ? 'text-[#00BFFF]' : 'text-gray-500'}`}
  >
    {React.cloneElement(icon, { size: 24 })}
    <span className="text-[10px] font-bold tracking-wider uppercase">{label}</span>
  </button>
));


// --- MAIN APP COMPONENT ---
function RefControl({ fixtureContext = null }) {
  const [activeTab, setActiveTab] = useState('home');
  const [matchHistory, setMatchHistory] = useState([]);

  const [settings, setSettings] = useState(loadSettings);
  const [lengthPresets, setLengthPresets] = useState(loadLengthPresets);

  const saveLengthPreset = (len) => {
    setLengthPresets(prev => {
      const next = [len, ...prev.filter(p => p !== len)].slice(0, 8);
      localStorage.setItem('refcontrol_length_presets', JSON.stringify(next));
      return next;
    });
  };

  const [config, setConfig] = useState(() => {
    const s = loadSettings();
    return {
      homeTeam: fixtureContext?.homeTeam || '',
      awayTeam: fixtureContext?.awayTeam || '',
      competition: fixtureContext?.competition || '',
      venue: fixtureContext?.venue || '',
      periods: fixtureContext?.periods || s.defaultPeriods,
      periodLength: fixtureContext?.periodLength || s.defaultPeriodLength,
    };
  });

  const [matchState, setMatchState] = useState({
    isActive: false,
    status: 'setup',
    period: 1,
    homeScore: 0,
    awayScore: 0,
    events: [],
    sinBins: []
  });

  const [elapsed, setElapsed] = useState(0);
  const [addedTime, setAddedTime] = useState(0);
  const [currentStoppage, setCurrentStoppage] = useState(0);

  const [isLocked, setIsLocked] = useState(false);
  const [activeModal, setActiveModal] = useState(null);
  const [modalData, setModalData] = useState({ team: 'home', player: '', reason: '', duration: settings.defaultSinBin });
  const [pendingYellow, setPendingYellow] = useState(null);

  const [lastEvent, setLastEvent] = useState(null);
  const undoTimeoutRef = useRef(null);

  const [selectedMatch, setSelectedMatch] = useState(null);
  const [confirmDeleteMatch, setConfirmDeleteMatch] = useState(null);
  const [shareCopied, setShareCopied] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);
  const reportRef = useRef(null);
  const liveRestored = useRef(false);

  // Hold-to-end-match (prevents accidental ends)
  const [endProgress, setEndProgress] = useState(0);
  const [endedEarly, setEndedEarly] = useState(false);
  const endHoldRef = useRef(null);
  const END_HOLD_MS = 1600;

  // Persist settings
  useEffect(() => {
    localStorage.setItem('refcontrol_settings', JSON.stringify(settings));
  }, [settings]);

  // Keep New Match defaults in sync with Settings (only when no match is active)
  useEffect(() => {
    if (fixtureContext) return; // fixture mode uses the tournament's period settings
    if (!matchState.isActive) {
      setConfig(c => ({ ...c, periods: settings.defaultPeriods, periodLength: settings.defaultPeriodLength }));
    }
  }, [settings.defaultPeriods, settings.defaultPeriodLength, activeTab, matchState.isActive]);

  // --- RESUME LIVE MATCH (persist + restore) ---
  useEffect(() => {
    if (fixtureContext) { liveRestored.current = true; return; }
    try {
      const raw = localStorage.getItem('refcontrol_live');
      if (raw) {
        const saved = JSON.parse(raw);
        if (saved?.matchState?.isActive) {
          setConfig(saved.config);
          const st = saved.matchState.status === 'period_ended' ? 'period_ended' : 'running';
          setMatchState({ ...saved.matchState, status: st });
          setElapsed(saved.elapsed || 0);
          setAddedTime(saved.addedTime || 0);
        }
      }
    } catch { /* ignore */ }
    liveRestored.current = true;
  }, []);

  useEffect(() => {
    if (!liveRestored.current || fixtureContext) return;
    if (matchState.isActive && matchState.status !== 'match_ended') {
      localStorage.setItem('refcontrol_live', JSON.stringify({ config, matchState, elapsed, addedTime }));
    } else {
      localStorage.removeItem('refcontrol_live');
    }
  }, [config, matchState, elapsed, addedTime]);

  const shareMatch = async (match) => {
    const url = `${window.location.origin}/share/${match.id}`;
    const text = `${match.config.homeTeam} ${match.matchState.homeScore}-${match.matchState.awayScore} ${match.config.awayTeam} — Tournevo match report`;
    try {
      if (navigator.share) {
        await navigator.share({ title: 'Tournevo Match Report', text, url });
      } else {
        await navigator.clipboard.writeText(url);
        setShareCopied(true);
        setTimeout(() => setShareCopied(false), 2500);
      }
    } catch (e) {
      // user cancelled or unsupported; ignore
    }
  };

  const handleDownloadPDF = async (match) => {
    setPdfBusy(true);
    try {
      const file = `Tournevo_${match.config.homeTeam}_vs_${match.config.awayTeam}.pdf`.replace(/\s+/g, '_');
      await downloadReportPDF(reportRef.current, file);
    } catch (e) {
      console.error('PDF export failed', e);
    }
    setPdfBusy(false);
  };

  // --- LOAD HISTORY FROM BACKEND ---
  const fetchHistory = async () => {
    try {
      const res = await axios.get(`${API}/matches`, { params: { owner_key: getOwnerKey() } });
      setMatchHistory(res.data);
    } catch (e) {
      console.error('Failed to load match history', e);
    }
  };

  useEffect(() => {
    fetchHistory();
  }, []);

  // --- TIMERS LOGIC ---
  useEffect(() => {
    let interval;
    if (matchState.status === 'running') {
      interval = setInterval(() => {
        setElapsed(prev => prev + 1);
        setMatchState(prevMatch => {
          const updatedSinBins = prevMatch.sinBins.map(sb => ({
            ...sb,
            remaining: Math.max(0, sb.remaining - 1)
          })).filter(sb => sb.remaining > 0);
          return { ...prevMatch, sinBins: updatedSinBins };
        });
      }, 1000);
    } else if (matchState.status === 'held') {
      interval = setInterval(() => setCurrentStoppage(s => s + 1), 1000);
    }
    return () => clearInterval(interval);
  }, [matchState.status]);

  // Watch for End of Period
  useEffect(() => {
    if (matchState.status !== 'running') return;
    const targetSeconds = (config.periodLength * 60) + addedTime;
    if (elapsed >= targetSeconds) {
      setMatchState(prev => ({ ...prev, status: 'period_ended' }));
    }
  }, [elapsed, matchState.status, config.periodLength, addedTime]);

  // --- ACTIONS ---
  const handleStartMatch = () => {
    if (!config.homeTeam || !config.awayTeam) return alert("Please enter team names.");
    saveLengthPreset(config.periodLength);
    setMatchState({
      isActive: true,
      status: 'running',
      period: 1,
      homeScore: 0,
      awayScore: 0,
      events: [],
      sinBins: []
    });
    setElapsed(0);
    setAddedTime(0);
  };

  const handlePointerDown = () => {
    if (matchState.status === 'running' && !isLocked) {
      setMatchState(prev => ({ ...prev, status: 'held' }));
    }
  };

  const handlePointerUp = () => {
    if (matchState.status === 'held') {
      setAddedTime(prev => prev + currentStoppage);
      if (currentStoppage > 5) {
        logEvent('stoppage', { reason: `Stoppage: ${formatTime(currentStoppage)}` });
      }
      setCurrentStoppage(0);
      setMatchState(prev => ({ ...prev, status: 'running' }));
    }
  };

  const startNextPeriod = () => {
    setMatchState(prev => ({ ...prev, period: prev.period + 1, status: 'running' }));
    setElapsed(0);
    setAddedTime(0);
  };

  const endMatch = async () => {
    if (settings.confirmEndMatch && !window.confirm('End the match and save the report?')) return;
    const finalState = { ...matchState, status: 'match_ended', isActive: false };
    setMatchState(finalState);

    // Tournament fixture mode: submit result back to the fixture
    if (fixtureContext) {
      const events = (finalState.events || [])
        .filter(e => ['goal', 'yellow', 'red', 'sin_bin', 'notes'].includes(e.type))
        .map(e => ({ minute: e.minute, type: e.type, team: e.team, player: e.player, reason: e.reason || '' }));
      try {
        const payload = { home_score: finalState.homeScore, away_score: finalState.awayScore, events };
        if (fixtureContext.submit) {
          await fixtureContext.submit(payload);
        } else {
          await tournamentApi.post(`/fixtures/${fixtureContext.fixtureId}/result`, payload);
        }
      } catch (e) {
        console.error('Failed to submit fixture result', e);
        alert('Could not submit the result. Check your connection and try again.');
        return;
      }
      fixtureContext.onComplete();
      return;
    }

    const payload = {
      config,
      matchState: {
        period: finalState.period,
        homeScore: finalState.homeScore,
        awayScore: finalState.awayScore,
        events: finalState.events,
        sinBins: [],
        status: 'match_ended'
      },
      totalAddedTime: addedTime
    };

    payload.owner_key = getOwnerKey();
    try {
      const res = await axios.post(`${API}/matches`, payload);
      setMatchHistory(prev => [res.data, ...prev]);
    } catch (e) {
      console.error('Failed to save match', e);
    }
    localStorage.removeItem('refcontrol_live');
    // reset config to defaults for the next match
    setConfig({
      homeTeam: '', awayTeam: '', competition: '', venue: '',
      periods: settings.defaultPeriods, periodLength: settings.defaultPeriodLength,
    });
    setActiveTab('matches');
  };

  // End the match early (jumps to the Full Time summary so the ref can save or discard)
  const endMatchEarly = () => {
    setIsLocked(false);
    setActiveModal(null);
    setEndedEarly(true);
    setMatchState(prev => ({ ...prev, period: config.periods, status: 'period_ended' }));
  };

  // Abandon an accidental/void match without saving it
  const abandonMatch = () => {
    if (fixtureContext) { fixtureContext.onComplete(); return; }
    localStorage.removeItem('refcontrol_live');
    setEndedEarly(false);
    setMatchState({ isActive: false, status: 'setup', period: 1, homeScore: 0, awayScore: 0, events: [], sinBins: [] });
    setElapsed(0);
    setAddedTime(0);
    setConfig(c => ({ ...c, homeTeam: '', awayTeam: '', competition: '', venue: '' }));
    setActiveTab('new_match');
  };

  const startEndHold = () => {
    if (endHoldRef.current) return;
    const startedAt = Date.now();
    endHoldRef.current = setInterval(() => {
      const pct = Math.min(1, (Date.now() - startedAt) / END_HOLD_MS);
      setEndProgress(pct);
      if (pct >= 1) {
        clearInterval(endHoldRef.current);
        endHoldRef.current = null;
        setEndProgress(0);
        endMatchEarly();
      }
    }, 30);
  };

  const cancelEndHold = () => {
    if (endHoldRef.current) {
      clearInterval(endHoldRef.current);
      endHoldRef.current = null;
    }
    setEndProgress(0);
  };

  // --- EVENT LOGGING ---
  const logEvent = (type, customData = {}) => {
    // Cumulative match minute: add full length of all completed periods
    const priorMinutes = (matchState.period - 1) * config.periodLength;
    const minuteStr = priorMinutes + Math.floor(elapsed / 60) + 1 + "'";
    const newEvent = {
      id: Date.now() + Math.random(),
      minute: minuteStr,
      period: matchState.period,
      type,
      team: customData.team || modalData.team,
      player: customData.player || modalData.player,
      reason: customData.reason || modalData.reason,
      ...customData
    };

    setMatchState(prev => {
      let updatedScore = { homeScore: prev.homeScore, awayScore: prev.awayScore };
      if (type === 'goal') {
        updatedScore = {
          homeScore: newEvent.team === 'home' ? prev.homeScore + 1 : prev.homeScore,
          awayScore: newEvent.team === 'away' ? prev.awayScore + 1 : prev.awayScore,
        };
      }

      let updatedSinBins = prev.sinBins;
      if (type === 'sin_bin') {
        updatedSinBins = [...prev.sinBins, {
          id: Date.now() + Math.random(),
          team: newEvent.team,
          player: newEvent.player,
          remaining: (customData.duration || modalData.duration) * 60
        }];
      }

      return {
        ...prev,
        ...updatedScore,
        events: [newEvent, ...prev.events],
        sinBins: updatedSinBins
      };
    });

    setLastEvent(newEvent);
    if (undoTimeoutRef.current) clearTimeout(undoTimeoutRef.current);
    undoTimeoutRef.current = setTimeout(() => setLastEvent(null), 10000);

    closeModal();
  };

  const saveModalEvent = () => {
    if (activeModal === 'yellow') {
      const hasYellow = matchState.events.some(e => e.type === 'yellow' && e.team === modalData.team && e.player === modalData.player);
      if (hasYellow && settings.autoSecondYellow) {
        setPendingYellow({ team: modalData.team, player: modalData.player, reason: modalData.reason });
        setActiveModal('second_yellow');
        return;
      }
    }
    logEvent(activeModal);
  };

  const handleSecondYellowChoice = (convert) => {
    logEvent('yellow', pendingYellow);
    if (convert) {
      setTimeout(() => logEvent('red', { ...pendingYellow, reason: 'Second Yellow' }), 100);
    }
    closeModal();
  };

  const undoLastEvent = () => {
    if (!lastEvent) return;
    setMatchState(prev => {
      const filteredEvents = prev.events.filter(e => e.id !== lastEvent.id);
      let updatedScore = { homeScore: prev.homeScore, awayScore: prev.awayScore };
      if (lastEvent.type === 'goal') {
        updatedScore = {
          homeScore: lastEvent.team === 'home' ? prev.homeScore - 1 : prev.homeScore,
          awayScore: lastEvent.team === 'away' ? prev.awayScore - 1 : prev.awayScore,
        };
      }
      let updatedSinBins = prev.sinBins;
      if (lastEvent.type === 'sin_bin') {
        updatedSinBins = prev.sinBins.filter(sb => !(sb.team === lastEvent.team && sb.player === lastEvent.player));
      }
      return { ...prev, ...updatedScore, events: filteredEvents, sinBins: updatedSinBins };
    });
    setLastEvent(null);
    clearTimeout(undoTimeoutRef.current);
  };

  const closeModal = () => {
    setActiveModal(null);
    setModalData({ team: 'home', player: '', reason: '', duration: settings.defaultSinBin });
    setPendingYellow(null);
  };

  const clearHistory = async () => {
    try {
      await axios.delete(`${API}/matches`, { params: { owner_key: getOwnerKey() } });
      setMatchHistory([]);
    } catch (e) {
      console.error('Failed to clear history', e);
    }
  };

  const deleteMatch = async (matchId) => {
    try {
      await axios.delete(`${API}/matches/${matchId}`, { params: { owner_key: getOwnerKey() } });
      setMatchHistory(prev => prev.filter(m => m.id !== matchId));
    } catch (e) {
      console.error('Failed to delete match', e);
    }
  };

  // --- TEAM-PANEL QUICK ACTION SYSTEM ---
  const [eventTeam, setEventTeam] = useState(null);        // 'home' | 'away'
  const [eventStep, setEventStep] = useState(null);        // menu | goal | yellow | red | sin_bin | substitution | note | done | second_yellow
  const [eventDraft, setEventDraft] = useState({});
  const [doneLabel, setDoneLabel] = useState('');
  const resumeRef = useRef('running');
  const quickGoal = fixtureContext?.quickGoal || settings.quickGoal;
  const teamName = (team) => team === 'home' ? config.homeTeam : config.awayTeam;
  const teamInitials = (name) => (name || '').trim().split(/\s+/).map(w => w[0]).join('').slice(0, 3).toUpperCase() || 'TBD';

  const openTeamPanel = (team) => {
    if (isLocked || ['period_ended', 'match_ended'].includes(matchState.status)) return;
    resumeRef.current = matchState.status === 'running' ? 'running' : 'paused';
    if (matchState.status === 'running' || matchState.status === 'held') {
      if (currentStoppage) { setAddedTime(p => p + currentStoppage); setCurrentStoppage(0); }
      setMatchState(p => ({ ...p, status: 'event_paused' }));
    }
    setEventTeam(team);
    setEventStep('menu');
    setEventDraft({ player: '', assist: '', reason: '', duration: settings.defaultSinBin, playerOff: '', playerOn: '' });
  };

  const closeEvent = (resume = true) => {
    setEventTeam(null); setEventStep(null); setEventDraft({});
    if (resume && resumeRef.current === 'running' && !['period_ended', 'match_ended'].includes(matchState.status)) {
      setMatchState(p => ({ ...p, status: 'running' }));
    }
  };

  const commitEvent = (type, extra = {}, label = '') => {
    if (type === 'yellow' && settings.autoSecondYellow && extra.player) {
      const hasYellow = matchState.events.some(e => e.type === 'yellow' && e.team === eventTeam && e.player === extra.player);
      if (hasYellow) { setEventDraft(d => ({ ...d, ...extra })); setEventStep('second_yellow'); return; }
    }
    logEvent(type, { team: eventTeam, ...extra });
    setDoneLabel(label || type.replace('_', ' '));
    setEventStep('done');
  };

  const confirmSecondYellow = (toRed) => {
    logEvent('yellow', { team: eventTeam, player: eventDraft.player, reason: eventDraft.reason });
    if (toRed) setTimeout(() => logEvent('red', { team: eventTeam, player: eventDraft.player, reason: 'Second Yellow' }), 80);
    setDoneLabel(toRed ? 'Second Yellow → Red' : 'Yellow Card');
    setEventStep('done');
  };

  // Event panel bottom-sheet (auto-pause team action system)
  const renderEventPanel = () => {
    if (!eventTeam) return null;
    const isHome = eventTeam === 'home';
    const accent = isHome ? '#00BFFF' : '#FF3B30';
    const name = teamName(eventTeam) || (isHome ? 'Home' : 'Away');
    const draftSet = (patch) => setEventDraft(d => ({ ...d, ...patch }));

    const Header = ({ title }) => (
      <div className="flex items-center justify-between p-4 border-b border-gray-800 bg-[#121212]">
        <button data-testid="event-back" onClick={() => (eventStep === 'menu' ? closeEvent() : setEventStep('menu'))} className="p-2 -ml-2 text-gray-400">
          <X size={24} />
        </button>
        <div className="text-center">
          <p className="text-[10px] font-bold uppercase tracking-widest" style={{ color: accent }}>{name}</p>
          <h2 className="text-sm font-bold tracking-widest uppercase">{title}</h2>
        </div>
        <div className="w-8" />
      </div>
    );

    const MenuBtn = ({ tid, label, icon, onClick }) => (
      <button data-testid={tid} onClick={onClick} className="bg-[#1C1C1E] border border-gray-800 rounded-2xl py-5 flex flex-col items-center justify-center gap-2 active:scale-95 hover:bg-gray-800 transition-all">
        <div className="h-7 flex items-center justify-center">{icon}</div>
        <span className="text-[11px] font-bold text-gray-200 tracking-wider">{label}</span>
      </button>
    );

    const SaveBtn = ({ disabled, onClick, label = 'SAVE' }) => (
      <div className="p-6 border-t border-gray-800">
        <button data-testid="event-save" disabled={disabled} onClick={onClick} style={{ background: accent }} className="w-full text-black font-bold text-lg py-4 rounded-xl disabled:opacity-40">
          {label}
        </button>
      </div>
    );

    return (
      <div className="absolute inset-0 z-50 flex flex-col bg-[#0A0A0A] animate-in slide-in-from-bottom duration-300" data-testid="event-panel">
        {eventStep === 'menu' && (
          <>
            <Header title="Log Event" />
            <div className="flex-1 p-5 overflow-y-auto">
              <div className="bg-[#1C1C1E] border border-gray-800 rounded-xl px-4 py-3 mb-5 flex items-center gap-2 justify-center">
                <Clock size={14} className="text-[#FFD400]" />
                <span className="text-xs font-bold text-[#FFD400] tracking-widest uppercase">Clock Paused</span>
              </div>
              <button
                data-testid="event-goal"
                onClick={() => (quickGoal ? commitEvent('goal', {}, 'Goal') : setEventStep('goal'))}
                style={{ borderColor: accent }}
                className="w-full bg-[#1C1C1E] border-2 rounded-2xl py-6 flex flex-col items-center justify-center gap-2 mb-4 active:scale-[0.98] transition-transform"
              >
                <div className="w-7 h-7 bg-white rounded-full border border-gray-300" />
                <span className="text-base font-black tracking-widest" style={{ color: accent }}>GOAL</span>
                {quickGoal && <span className="text-[10px] text-gray-500 uppercase tracking-wider">Quick goal · one tap</span>}
              </button>
              <div className="grid grid-cols-3 gap-3">
                <MenuBtn tid="event-yellow" label="YELLOW" icon={<CardIcon color="yellow" className="w-5 h-7" />} onClick={() => setEventStep('yellow')} />
                <MenuBtn tid="event-red" label="RED" icon={<CardIcon color="red" className="w-5 h-7" />} onClick={() => setEventStep('red')} />
                <MenuBtn tid="event-sinbin" label="SIN BIN" icon={<CardIcon color="success" className="w-5 h-7" />} onClick={() => setEventStep('sin_bin')} />
                <MenuBtn tid="event-sub" label="SUB" icon={<RotateCcw size={22} className="text-[#00D26A]" />} onClick={() => setEventStep('substitution')} />
                <MenuBtn tid="event-note" label="NOTE" icon={<FileText size={22} className="text-gray-300" />} onClick={() => setEventStep('note')} />
              </div>
            </div>
          </>
        )}

        {eventStep === 'goal' && (
          <>
            <Header title="Goal" />
            <div className="flex-1 p-6 space-y-6 overflow-y-auto">
              <Input testId="event-goal-scorer" label="Scorer Number" type="number" value={eventDraft.player} onChange={v => draftSet({ player: v })} placeholder="e.g. 9" />
              <Input testId="event-goal-assist" label="Assist (Optional)" type="number" value={eventDraft.assist} onChange={v => draftSet({ assist: v })} placeholder="e.g. 7" />
            </div>
            <SaveBtn onClick={() => commitEvent('goal', { player: eventDraft.player, assist: eventDraft.assist, reason: eventDraft.assist ? `Assist #${eventDraft.assist}` : '' }, 'Goal')} label="LOG GOAL" />
          </>
        )}

        {(eventStep === 'yellow' || eventStep === 'red') && (
          <>
            <Header title={eventStep === 'yellow' ? 'Yellow Card' : 'Red Card'} />
            <div className="flex-1 p-6 space-y-6 overflow-y-auto">
              <Input testId="event-card-player" label="Player Number" type="number" value={eventDraft.player} onChange={v => draftSet({ player: v })} placeholder="e.g. 4" />
              <Input testId="event-card-reason" label="Reason (Optional)" value={eventDraft.reason} onChange={v => draftSet({ reason: v })} placeholder="e.g. Reckless tackle" />
            </div>
            <SaveBtn disabled={!eventDraft.player} onClick={() => commitEvent(eventStep, { player: eventDraft.player, reason: eventDraft.reason }, eventStep === 'yellow' ? 'Yellow Card' : 'Red Card')} label={eventStep === 'yellow' ? 'LOG YELLOW' : 'LOG RED'} />
          </>
        )}

        {eventStep === 'sin_bin' && (
          <>
            <Header title="Sin Bin" />
            <div className="flex-1 p-6 space-y-6 overflow-y-auto">
              <Input testId="event-sinbin-player" label="Player Number" type="number" value={eventDraft.player} onChange={v => draftSet({ player: v })} placeholder="e.g. 11" />
              <Stepper testId="event-sinbin-duration" label="Duration (Mins)" value={eventDraft.duration} onChange={v => draftSet({ duration: v })} min={1} max={20} />
              <Input testId="event-sinbin-reason" label="Reason (Optional)" value={eventDraft.reason} onChange={v => draftSet({ reason: v })} placeholder="Enter details..." />
            </div>
            <SaveBtn disabled={!eventDraft.player} onClick={() => commitEvent('sin_bin', { player: eventDraft.player, duration: eventDraft.duration, reason: eventDraft.reason }, 'Sin Bin')} label="START SIN BIN" />
          </>
        )}

        {eventStep === 'substitution' && (
          <>
            <Header title="Substitution" />
            <div className="flex-1 p-6 space-y-6 overflow-y-auto">
              <Input testId="event-sub-off" label="Player Off" type="number" value={eventDraft.playerOff} onChange={v => draftSet({ playerOff: v })} placeholder="e.g. 9" />
              <Input testId="event-sub-on" label="Player On" type="number" value={eventDraft.playerOn} onChange={v => draftSet({ playerOn: v })} placeholder="e.g. 14" />
            </div>
            <SaveBtn disabled={!eventDraft.playerOff && !eventDraft.playerOn} onClick={() => commitEvent('substitution', { player: eventDraft.playerOff, reason: `#${eventDraft.playerOff} off, #${eventDraft.playerOn} on` }, 'Substitution')} label="LOG SUB" />
          </>
        )}

        {eventStep === 'note' && (
          <>
            <Header title="Note" />
            <div className="flex-1 p-6 space-y-6 overflow-y-auto">
              <Input testId="event-note-text" label="Details" value={eventDraft.reason} onChange={v => draftSet({ reason: v })} placeholder="Enter note..." />
            </div>
            <SaveBtn disabled={!eventDraft.reason} onClick={() => commitEvent('notes', { reason: eventDraft.reason }, 'Note')} label="SAVE NOTE" />
          </>
        )}

        {eventStep === 'second_yellow' && (
          <div className="flex-1 flex flex-col items-center justify-center p-6">
            <AlertTriangle className="w-16 h-16 text-[#FFD400] mb-4" />
            <h2 className="text-2xl font-black text-[#FFD400] tracking-wider mb-2 text-center">SECOND YELLOW</h2>
            <p className="text-center text-gray-300 mb-8">Player #{eventDraft.player} already cautioned.<br />Convert to a red card?</p>
            <div className="w-full space-y-4">
              <button data-testid="event-second-yellow-yes" onClick={() => confirmSecondYellow(true)} className="w-full bg-[#FF3B30] text-white font-bold py-4 rounded-xl">YES, RED CARD</button>
              <button data-testid="event-second-yellow-no" onClick={() => confirmSecondYellow(false)} className="w-full bg-[#1C1C1E] border border-gray-800 text-gray-300 font-bold py-4 rounded-xl">NO, JUST YELLOW</button>
            </div>
          </div>
        )}

        {eventStep === 'done' && (
          <div className="flex-1 flex flex-col items-center justify-center p-6 animate-in zoom-in duration-200">
            <div className="w-20 h-20 rounded-full flex items-center justify-center mb-5" style={{ background: `${accent}22`, border: `2px solid ${accent}` }}>
              <Check size={40} style={{ color: accent }} />
            </div>
            <h2 className="text-2xl font-black tracking-wider mb-1 text-center uppercase">{doneLabel}</h2>
            <p className="text-sm text-gray-400 mb-10">{name}</p>
            <div className="w-full space-y-3">
              <button data-testid="event-done-resume" onClick={() => closeEvent(true)} style={{ background: accent }} className="w-full text-black font-bold py-4 rounded-xl tracking-wider">RESUME MATCH</button>
              <button data-testid="event-done-another" onClick={() => { setEventDraft({ player: '', assist: '', reason: '', duration: settings.defaultSinBin, playerOff: '', playerOn: '' }); setEventStep('menu'); }} className="w-full bg-[#1C1C1E] border border-gray-800 text-gray-300 font-bold py-3 rounded-xl text-sm tracking-wider">LOG ANOTHER EVENT</button>
            </div>
          </div>
        )}
      </div>
    );
  };

  // --- RENDERERS ---
  const renderBottomNav = () => {
    if (fixtureContext) return null;
    return (
    <div className="flex items-center justify-around bg-[#121212] border-t border-gray-800 p-4 pb-6">
      <NavBtn testId="nav-home" icon={<Home />} label="Home" isActive={activeTab === 'home'} onClick={() => setActiveTab('home')} />
      <NavBtn testId="nav-matches" icon={<List />} label="Matches" isActive={activeTab === 'matches'} onClick={() => setActiveTab('matches')} />
      <button
        data-testid="nav-new-match"
        onClick={() => setActiveTab('new_match')}
        className="relative -top-6 bg-[#00BFFF] p-4 rounded-full text-black shadow-[0_0_15px_rgba(0,191,255,0.4)]"
      >
        <Plus size={32} />
      </button>
      <NavBtn testId="nav-stats" icon={<BarChart2 />} label="Stats" isActive={activeTab === 'stats'} onClick={() => setActiveTab('stats')} />
      <NavBtn testId="nav-settings" icon={<Settings />} label="Settings" isActive={activeTab === 'settings'} onClick={() => setActiveTab('settings')} />
    </div>
    );
  };

  const renderSetup = () => (
    <div className="flex flex-col h-full bg-[#0A0A0A] text-white overflow-y-auto">
      <div className="p-6 border-b border-gray-800 flex items-center justify-center relative">
        {fixtureContext && (
          <button data-testid="fixture-cancel" onClick={() => fixtureContext.onComplete()} className="absolute left-4 text-gray-400 text-sm">Cancel</button>
        )}
        <h1 className="text-xl font-bold tracking-wider">{fixtureContext ? 'REFEREE FIXTURE' : 'NEW MATCH'}</h1>
      </div>

      {fixtureContext && (
        <div className="mx-6 mt-4 bg-[#00BFFF]/10 border border-[#00BFFF]/30 rounded-xl p-3 text-center">
          <p className="text-xs text-[#00BFFF] font-bold">{fixtureContext.competition || 'Tournament fixture'}</p>
          <p className="text-sm font-bold mt-1">{fixtureContext.homeTeam} vs {fixtureContext.awayTeam}</p>
          <p className="text-[10px] text-gray-500 mt-1">Final score & cards will auto-fill the tournament report</p>
        </div>
      )}

      <div className="p-6 space-y-8 flex-1">
        <div className="space-y-4">
          <Input testId="input-home-team" label="Home Team" value={config.homeTeam} onChange={v => setConfig({ ...config, homeTeam: v })} />
          <Input testId="input-away-team" label="Away Team" value={config.awayTeam} onChange={v => setConfig({ ...config, awayTeam: v })} />
          <Input testId="input-competition" label="Competition (Optional)" value={config.competition} onChange={v => setConfig({ ...config, competition: v })} />
          <Input testId="input-venue" label="Venue (Optional)" value={config.venue} onChange={v => setConfig({ ...config, venue: v })} />
        </div>

        <div className="space-y-6 pt-6 border-t border-gray-800">
          <Stepper testId="stepper-periods" label="Periods" value={config.periods} onChange={v => setConfig({ ...config, periods: v })} min={1} max={4} />
          <div className="space-y-3">
            <Stepper testId="stepper-length" label="Length (Mins)" value={config.periodLength} onChange={v => setConfig({ ...config, periodLength: v })} min={1} max={90} />
            <div className="flex flex-wrap gap-2" data-testid="length-presets">
              {lengthPresets.map(p => (
                <button
                  key={p}
                  data-testid={`length-preset-${p}`}
                  onClick={() => setConfig({ ...config, periodLength: p })}
                  className={`px-4 py-2 rounded-full text-xs font-bold tracking-wider border transition-colors ${
                    config.periodLength === p
                      ? 'bg-[#00BFFF] text-black border-[#00BFFF]'
                      : 'bg-[#1C1C1E] text-gray-300 border-gray-800 hover:border-gray-600'
                  }`}
                >
                  {p}'
                </button>
              ))}
            </div>
            {lengthPresets.includes(config.periodLength) ? (
              <p className="text-[11px] text-gray-600 flex items-center gap-1.5 pt-1">
                <Check size={13} className="text-[#00D26A]" /> {config.periodLength}' is saved as a preset
              </p>
            ) : (
              <button
                data-testid="save-length-btn"
                onClick={() => saveLengthPreset(config.periodLength)}
                className="w-full bg-[#1C1C1E] border border-[#00BFFF]/40 text-[#00BFFF] font-bold text-xs tracking-wider py-3 rounded-xl flex items-center justify-center gap-2 hover:bg-[#00BFFF]/10 transition-colors"
              >
                <Plus size={14} /> SAVE {config.periodLength}' AS PRESET
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="p-6">
        <button
          data-testid="start-match-btn"
          onClick={handleStartMatch}
          className="w-full bg-[#00BFFF] text-black font-bold text-lg py-4 rounded-2xl shadow-[0_0_20px_rgba(0,191,255,0.3)]"
        >
          START MATCH
        </button>
      </div>
      {renderBottomNav()}
    </div>
  );

  const renderLiveMatch = () => {
    const isHeld = matchState.status === 'held';
    const isPeriodEnded = matchState.status === 'period_ended';
    const regSeconds = config.periodLength * 60;
    const totalSeconds = regSeconds + addedTime;
    const inAddedTime = elapsed >= regSeconds;
    let timeRemainingValue = Math.max(0, totalSeconds - elapsed);

    return (
      <div className="flex flex-col h-full bg-[#0A0A0A] text-white relative select-none">
        <div className="grid grid-cols-3 items-center p-4 border-b border-gray-800 bg-[#121212]">
          <div className="text-center">
            <p className="text-xs text-gray-400 truncate mb-1">{config.homeTeam}</p>
            <p data-testid="home-score" className="text-3xl font-bold text-[#00BFFF]">{matchState.homeScore}</p>
          </div>
          <div className="text-center flex flex-col items-center">
            <p className="text-[10px] text-gray-500 font-bold uppercase tracking-widest mb-1">Period {matchState.period}/{config.periods}</p>
            <p className="text-xl font-black text-gray-600">-</p>
          </div>
          <div className="text-center">
            <p className="text-xs text-gray-400 truncate mb-1">{config.awayTeam}</p>
            <p data-testid="away-score" className="text-3xl font-bold text-[#FF3B30]">{matchState.awayScore}</p>
          </div>
        </div>

        <div className="flex-1 flex flex-col items-center justify-center p-4">
          <h1 data-testid="match-clock" className={`text-8xl font-black tracking-tighter mb-2 tabular-nums ${inAddedTime ? 'text-[#FFD400]' : 'text-white'}`}>
            {formatTime(elapsed)}
          </h1>
          <p className="text-sm font-bold tracking-widest text-gray-400">
            {formatTime(timeRemainingValue)} LEFT
          </p>

          <div className="mt-4 bg-[#1C1C1E] rounded-full px-6 py-2 border border-gray-800 flex items-center space-x-2">
            <span className="text-xs font-bold text-gray-500 uppercase tracking-widest">Added Time</span>
            <span className="text-lg font-bold text-[#FFD400]">+{formatTime(addedTime)}</span>
          </div>

          {matchState.sinBins.length > 0 && (
            <div className="w-full mt-4 flex justify-center gap-2 flex-wrap px-4">
              {matchState.sinBins.map(sb => (
                <div key={sb.id} className="bg-gray-900 border border-gray-800 rounded-lg px-3 py-1 flex items-center space-x-2">
                  <CardIcon color="success" className="w-3 h-4" />
                  <span className="text-xs font-bold">#{sb.player} {sb.team === 'home' ? 'H' : 'A'}</span>
                  <span className="text-xs text-[#00D26A] font-mono">{formatTime(sb.remaining)}</span>
                </div>
              ))}
            </div>
          )}

          <div className="relative mt-8 flex items-center justify-center w-full h-48">
            <div
              data-testid="hold-to-stop"
              onPointerDown={handlePointerDown}
              onPointerUp={handlePointerUp}
              onPointerLeave={handlePointerUp}
              onContextMenu={e => e.preventDefault()}
              className={`touch-none w-44 h-44 rounded-full flex flex-col items-center justify-center cursor-pointer transition-all duration-200 border-[6px]
                ${isHeld
                  ? 'border-[#FF3B30] bg-[#FF3B30] bg-opacity-10 scale-95 shadow-[0_0_30px_rgba(255,59,48,0.5)]'
                  : isLocked
                    ? 'border-gray-800 bg-gray-900 opacity-50'
                    : 'border-[#00BFFF] bg-[#00BFFF] bg-opacity-5 shadow-[0_0_20px_rgba(0,191,255,0.2)]'
                }`}
            >
              {isHeld ? (
                <>
                  <Square className="w-10 h-10 text-[#FF3B30] mb-2 fill-current" />
                  <p className="text-[#FF3B30] font-bold text-sm tracking-widest">STOPPED</p>
                  <p className="text-2xl font-black text-[#FF3B30]">{formatTime(currentStoppage)}</p>
                </>
              ) : isLocked ? (
                <Lock className="w-12 h-12 text-gray-600" />
              ) : (
                <p className="text-[#00BFFF] font-bold text-sm tracking-widest uppercase text-center leading-tight">
                  Hold to<br />Stop Time
                </p>
              )}
            </div>
          </div>
        </div>

        {lastEvent && !isLocked && (
          <div className="absolute top-24 left-1/2 -translate-x-1/2 bg-[#1C1C1E] border border-gray-700 px-4 py-2 rounded-full shadow-lg flex items-center space-x-3 z-30 animate-in fade-in slide-in-from-top-4">
            <span className="text-xs text-gray-300 font-medium">Event saved</span>
            <div className="w-px h-4 bg-gray-700"></div>
            <button data-testid="undo-event-btn" onClick={undoLastEvent} className="flex items-center space-x-1 text-[#FFD400] hover:text-white">
              <RotateCcw size={14} />
              <span className="text-xs font-bold uppercase tracking-wider">Undo</span>
            </button>
          </div>
        )}

        <div className={`grid grid-cols-2 gap-3 px-4 pb-4 transition-opacity ${isLocked ? 'opacity-0 pointer-events-none' : 'opacity-100'}`}>
          <button
            data-testid="team-panel-home"
            onClick={() => openTeamPanel('home')}
            className="bg-[#1C1C1E] border-2 border-[#00BFFF]/40 rounded-2xl py-5 px-3 flex flex-col items-center justify-center gap-2 active:scale-[0.97] hover:bg-[#00BFFF]/5 transition-all"
          >
            <span className="w-11 h-11 rounded-full bg-[#00BFFF]/15 border border-[#00BFFF]/50 flex items-center justify-center text-[#00BFFF] font-black text-sm">{teamInitials(config.homeTeam)}</span>
            <span className="text-sm font-bold text-white truncate max-w-full">{config.homeTeam || 'Home'}</span>
            <span className="text-[9px] text-[#00BFFF] font-bold uppercase tracking-widest">Tap to log</span>
          </button>
          <button
            data-testid="team-panel-away"
            onClick={() => openTeamPanel('away')}
            className="bg-[#1C1C1E] border-2 border-[#FF3B30]/40 rounded-2xl py-5 px-3 flex flex-col items-center justify-center gap-2 active:scale-[0.97] hover:bg-[#FF3B30]/5 transition-all"
          >
            <span className="w-11 h-11 rounded-full bg-[#FF3B30]/15 border border-[#FF3B30]/50 flex items-center justify-center text-[#FF3B30] font-black text-sm">{teamInitials(config.awayTeam)}</span>
            <span className="text-sm font-bold text-white truncate max-w-full">{config.awayTeam || 'Away'}</span>
            <span className="text-[9px] text-[#FF3B30] font-bold uppercase tracking-widest">Tap to log</span>
          </button>
        </div>

        <div className={`flex gap-2 px-4 pb-6 transition-opacity ${isLocked ? 'opacity-0 pointer-events-none' : 'opacity-100'}`}>
          <button data-testid="action-notes" onClick={() => setActiveModal('notes')} className="flex-1 bg-[#1C1C1E] border border-gray-800 rounded-xl py-3 flex items-center justify-center space-x-2">
            <FileText size={16} className="text-gray-400" />
            <span className="text-xs font-bold text-gray-300 tracking-wider">NOTES</span>
          </button>
          <button data-testid="lock-btn" onDoubleClick={() => setIsLocked(true)} className="flex-1 bg-[#1C1C1E] border border-gray-800 rounded-xl py-3 flex items-center justify-center space-x-2">
            <Lock size={16} className="text-gray-400" />
            <span className="text-xs font-bold text-gray-300 tracking-wider">LOCK (2x)</span>
          </button>
          <button
            data-testid="end-match-hold"
            onPointerDown={startEndHold}
            onPointerUp={cancelEndHold}
            onPointerLeave={cancelEndHold}
            onContextMenu={e => e.preventDefault()}
            className="relative flex-1 overflow-hidden bg-[#1C1C1E] border border-[#FF3B30]/40 rounded-xl py-3 flex items-center justify-center space-x-2 touch-none active:scale-[0.98] transition-transform"
          >
            <div
              className="absolute inset-y-0 left-0 bg-[#FF3B30]/30"
              style={{ width: `${endProgress * 100}%`, transition: endProgress === 0 ? 'width 0.2s ease' : 'none' }}
            />
            <Power size={16} className="text-[#FF3B30] relative z-10" />
            <span className="text-xs font-bold text-[#FF3B30] tracking-wider relative z-10">
              {endProgress > 0 ? 'HOLD…' : 'END'}
            </span>
          </button>
        </div>

        {isLocked && (
          <div className="absolute bottom-10 left-0 right-0 flex justify-center">
            <button data-testid="unlock-btn" onDoubleClick={() => setIsLocked(false)} className="bg-gray-900 border-2 border-gray-700 rounded-full py-3 px-6 flex items-center space-x-2 shadow-2xl animate-pulse">
              <Unlock size={20} className="text-white" />
              <span className="text-sm font-bold text-white tracking-widest uppercase">Double Tap to Unlock</span>
            </button>
          </div>
        )}

        {isPeriodEnded && (
          <div className="absolute inset-0 bg-[#0A0A0A] z-40 flex flex-col items-center justify-center p-6 text-center animate-in zoom-in duration-300">
            <Clock className="w-16 h-16 text-[#FFD400] mb-4" />
            <h2 className="text-3xl font-black tracking-widest mb-2 uppercase">End of Period {matchState.period}</h2>
            <p className="text-gray-400 mb-8">Period Complete.<br />Added Time Played: +{formatTime(addedTime)}</p>

            {matchState.period < config.periods ? (
              <button data-testid="next-period-btn" onClick={startNextPeriod} className="w-full bg-[#00BFFF] text-black font-bold py-4 rounded-xl shadow-[0_0_20px_rgba(0,191,255,0.3)] tracking-wider">
                START PERIOD {matchState.period + 1}
              </button>
            ) : (
              <div className="w-full space-y-4">
                <h3 className="text-xl font-bold text-[#00BFFF] mb-6">FULL TIME</h3>
                <div className="text-4xl font-black mb-8">{config.homeTeam} {matchState.homeScore} - {matchState.awayScore} {config.awayTeam}</div>
                <button data-testid="match-summary-btn" onClick={endMatch} className="w-full bg-[#00BFFF] text-black font-bold py-4 rounded-xl shadow-[0_0_20px_rgba(0,191,255,0.3)] tracking-wider">
                  {fixtureContext ? 'SUBMIT TO TOURNAMENT' : 'SAVE MATCH REPORT'}
                </button>
                <button data-testid="discard-match-btn" onClick={abandonMatch} className="w-full text-gray-500 font-bold py-2 text-xs tracking-widest uppercase hover:text-[#FF3B30] transition-colors">
                  {fixtureContext ? 'Cancel (back to tournament)' : "Discard (don't save)"}
                </button>
              </div>
            )}
          </div>
        )}

        {activeModal && renderModals()}
        {renderEventPanel()}
      </div>
    );
  };

  const renderModals = () => {
    if (activeModal === 'second_yellow') {
      return (
        <div className="absolute inset-0 bg-black bg-opacity-95 z-50 flex flex-col items-center justify-center p-6 animate-in fade-in">
          <AlertTriangle className="w-16 h-16 text-[#FFD400] mb-4" />
          <h2 className="text-2xl font-black text-[#FFD400] tracking-wider mb-2 text-center">SECOND YELLOW DETECTED</h2>
          <p className="text-center text-gray-300 mb-8">Player #{pendingYellow?.player} already cautioned.<br />Convert to red card?</p>
          <div className="w-full space-y-4">
            <button data-testid="second-yellow-yes" onClick={() => handleSecondYellowChoice(true)} className="w-full bg-[#FF3B30] text-white font-bold py-4 rounded-xl">YES, RED CARD</button>
            <button data-testid="second-yellow-no" onClick={() => handleSecondYellowChoice(false)} className="w-full bg-[#1C1C1E] border border-gray-800 text-gray-300 font-bold py-4 rounded-xl">NO, JUST YELLOW</button>
          </div>
        </div>
      );
    }

    const isNotes = activeModal === 'notes';
    const title = isNotes ? 'Add Note' : `Add ${activeModal.replace('_', ' ')}`;

    return (
      <div className="absolute inset-0 bg-[#0A0A0A] z-50 flex flex-col animate-in slide-in-from-bottom duration-300">
        <div className="flex items-center justify-between p-4 border-b border-gray-800 bg-[#121212]">
          <button data-testid="modal-close" onClick={closeModal} className="p-2 -ml-2 text-gray-400"><X size={24} /></button>
          <h2 className="text-sm font-bold tracking-widest uppercase">{title}</h2>
          <div className="w-8"></div>
        </div>

        <div className="flex-1 p-6 space-y-6 overflow-y-auto">
          {!isNotes && (
            <div className="flex bg-[#1C1C1E] p-1 rounded-xl">
              <button data-testid="modal-team-home" onClick={() => setModalData({ ...modalData, team: 'home' })} className={`flex-1 py-3 rounded-lg text-xs font-bold tracking-wider ${modalData.team === 'home' ? 'bg-[#00BFFF] text-black' : 'text-gray-400'}`}>HOME</button>
              <button data-testid="modal-team-away" onClick={() => setModalData({ ...modalData, team: 'away' })} className={`flex-1 py-3 rounded-lg text-xs font-bold tracking-wider ${modalData.team === 'away' ? 'bg-[#00BFFF] text-black' : 'text-gray-400'}`}>AWAY</button>
            </div>
          )}

          {!isNotes && (
            <Input testId="modal-player" label="Player Number" type="number" value={modalData.player} onChange={v => setModalData({ ...modalData, player: v })} placeholder="e.g. 9" />
          )}

          {activeModal === 'sin_bin' && (
            <Stepper testId="modal-duration" label="Duration (Mins)" value={modalData.duration} onChange={v => setModalData({ ...modalData, duration: v })} min={1} max={20} />
          )}

          {activeModal !== 'goal' && (
            <Input testId="modal-reason" label={isNotes ? "Details" : "Reason (Optional)"} value={modalData.reason} onChange={v => setModalData({ ...modalData, reason: v })} placeholder="Enter details..." />
          )}
        </div>

        <div className="p-6 border-t border-gray-800">
          <button
            data-testid="modal-save"
            onClick={saveModalEvent}
            disabled={!isNotes && !modalData.player}
            className="w-full bg-[#00BFFF] text-black font-bold text-lg py-4 rounded-xl disabled:opacity-50"
          >
            SAVE
          </button>
        </div>
      </div>
    );
  };

  const renderDashboard = () => (
    <div className="flex flex-col h-full bg-[#0A0A0A] text-white">
      <div className="p-6 border-b border-gray-800">
        <h1 className="text-xl font-bold tracking-wider">DASHBOARD</h1>
      </div>
      <div className="flex-1 p-6 space-y-6 overflow-y-auto">
        <div className="bg-[#1C1C1E] border border-gray-800 rounded-2xl p-6 flex flex-col items-center text-center">
          <ShieldAlert className="w-12 h-12 text-[#00BFFF] mb-2" />
          <h2 className="text-lg font-bold">Welcome to Tournevo</h2>
          <p className="text-sm text-gray-500 mt-2">Ready for your next match?</p>
          <button data-testid="dashboard-start-btn" onClick={() => setActiveTab('new_match')} className="mt-4 bg-[#00BFFF] text-black font-bold px-6 py-2 rounded-full text-sm">START NEW MATCH</button>
        </div>

        <button data-testid="tournament-mode-btn" onClick={() => { window.location.href = '/tournaments'; }}
          className="w-full bg-gradient-to-r from-[#00BFFF]/20 to-[#1C1C1E] border border-[#00BFFF]/40 rounded-2xl p-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Trophy className="w-7 h-7 text-[#00BFFF]" />
            <div className="text-left">
              <p className="font-bold">Tournament Mode</p>
              <p className="text-xs text-gray-400">Run competitions, fixtures & live tables</p>
            </div>
          </div>
          <span className="text-[#00BFFF] text-xl">›</span>
        </button>

        <div className="grid grid-cols-2 gap-4">
          <StatCard testId="stat-matches" label="Matches Refereed" value={matchHistory.length} />
          <StatCard testId="stat-goals" label="Total Goals" value={matchHistory.reduce((acc, m) => acc + m.matchState.homeScore + m.matchState.awayScore, 0)} />
        </div>
      </div>
      {renderBottomNav()}
    </div>
  );

  const renderHistory = () => (
    <div className="flex flex-col h-full bg-[#0A0A0A] text-white">
      <div className="p-6 border-b border-gray-800 flex justify-between items-center">
        <h1 className="text-xl font-bold tracking-wider">MATCH HISTORY</h1>
        <button data-testid="clear-history-btn" onClick={clearHistory} className="text-xs text-gray-500 uppercase tracking-widest">Clear</button>
      </div>
      <div className="flex-1 p-4 overflow-y-auto space-y-4">
        {matchHistory.length === 0 ? (
          <p className="text-center text-gray-500 p-8 text-sm">No matches recorded yet.</p>
        ) : (
          matchHistory.map(match => (
            <div key={match.id} data-testid={`match-card-${match.id}`} onClick={() => { setSelectedMatch(match); setShareCopied(false); }} className="bg-[#1C1C1E] border border-gray-800 rounded-xl p-4 cursor-pointer hover:border-gray-600 active:scale-[0.99] transition-all">
              <div className="flex justify-between items-center mb-2">
                <p className="text-xs text-gray-500">{match.date} • {match.config.competition || 'Friendly'}</p>
                <button data-testid={`delete-match-${match.id}`} onClick={(e) => { e.stopPropagation(); setConfirmDeleteMatch(match); }} className="p-1 -mr-1 text-gray-600 hover:text-[#FF3B30] transition-colors"><Trash2 size={15} /></button>
              </div>
              <div className="flex justify-between items-center">
                <span className="font-bold">{match.config.homeTeam}</span>
                <span className="text-xl font-black bg-black px-3 py-1 rounded-lg">{match.matchState.homeScore} - {match.matchState.awayScore}</span>
                <span className="font-bold">{match.config.awayTeam}</span>
              </div>
              <p className="text-[10px] text-gray-600 uppercase tracking-widest mt-3 text-center">Tap for report</p>
            </div>
          ))
        )}
      </div>
      {confirmDeleteMatch && (
        <div className="absolute inset-0 bg-black/80 z-[60] flex items-center justify-center p-4" data-testid="delete-match-confirm">
          <div className="bg-[#121212] border border-gray-800 rounded-3xl w-full max-w-xs p-6 text-center">
            <div className="w-14 h-14 rounded-full bg-[#FF3B30]/15 border-2 border-[#FF3B30] flex items-center justify-center mx-auto mb-4"><Trash2 size={24} className="text-[#FF3B30]" /></div>
            <h2 className="text-base font-black">Delete this match?</h2>
            <p className="text-sm text-gray-400 mt-2">{confirmDeleteMatch.config.homeTeam} {confirmDeleteMatch.matchState.homeScore}–{confirmDeleteMatch.matchState.awayScore} {confirmDeleteMatch.config.awayTeam} will be permanently removed.</p>
            <div className="flex gap-3 mt-6">
              <button data-testid="delete-match-no" onClick={() => setConfirmDeleteMatch(null)} className="flex-1 bg-[#1C1C1E] border border-gray-700 text-gray-300 font-bold py-3 rounded-xl">Cancel</button>
              <button data-testid="delete-match-yes" onClick={() => { deleteMatch(confirmDeleteMatch.id); setConfirmDeleteMatch(null); }} className="flex-1 bg-[#FF3B30] text-white font-bold py-3 rounded-xl">Delete</button>
            </div>
          </div>
        </div>
      )}
      {renderBottomNav()}
      {selectedMatch && renderMatchDetail()}
    </div>
  );

  const renderMatchDetail = () => (
    <div className="absolute inset-0 bg-[#0A0A0A] z-50 flex flex-col animate-in slide-in-from-bottom duration-300">
      <div className="flex items-center justify-between p-4 border-b border-gray-800 bg-[#121212]">
        <button data-testid="detail-close" onClick={() => setSelectedMatch(null)} className="p-2 -ml-2 text-gray-400"><X size={24} /></button>
        <h2 className="text-sm font-bold tracking-widest uppercase">Match Report</h2>
        <div className="w-8"></div>
      </div>
      <div className="flex-1 p-5 overflow-y-auto">
        <div ref={reportRef} className="bg-[#0A0A0A] p-1">
          <MatchReport match={selectedMatch} />
        </div>
      </div>
      <div className="p-5 border-t border-gray-800 flex gap-3">
        <button
          data-testid="download-pdf-btn"
          onClick={() => handleDownloadPDF(selectedMatch)}
          disabled={pdfBusy}
          className="flex-1 bg-[#1C1C1E] border border-gray-800 text-white font-bold text-sm py-4 rounded-xl flex items-center justify-center gap-2 disabled:opacity-50"
        >
          <Download size={18} /> {pdfBusy ? 'SAVING…' : 'PDF'}
        </button>
        <button
          data-testid="share-report-btn"
          onClick={() => shareMatch(selectedMatch)}
          className="flex-[2] bg-[#00BFFF] text-black font-bold text-base py-4 rounded-xl flex items-center justify-center gap-2"
        >
          {shareCopied ? (<><Check size={18} /> COPIED</>) : (<><Share2 size={18} /> SHARE</>)}
        </button>
      </div>
    </div>
  );

  // --- ROUTING ---
  return (
    <div className="min-h-screen bg-black flex items-center justify-center font-sans">
      <div className="w-full h-[100dvh] max-w-md bg-[#0A0A0A] sm:border border-gray-800 sm:rounded-[3rem] shadow-2xl overflow-hidden relative">
        {matchState.isActive ? renderLiveMatch() : (
          <>
            {activeTab === 'home' && renderDashboard()}
            {activeTab === 'matches' && renderHistory()}
            {activeTab === 'new_match' && renderSetup()}
            {activeTab === 'stats' && <StatsView matchHistory={matchHistory} BottomNav={renderBottomNav()} />}
            {activeTab === 'settings' && (
              <SettingsView
                settings={settings}
                onChange={setSettings}
                onClearHistory={clearHistory}
                matchCount={matchHistory.length}
                BottomNav={renderBottomNav()}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
}

function RequireAuth({ children }) {
  const { user, ready } = useAuth();
  if (!ready) return <div className="min-h-screen bg-[#0A0A0A] text-gray-500 flex items-center justify-center font-sans">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function RefereeMatch() {
  const { id } = useParams();
  const nav = useNavigate();
  const [ctx, setCtx] = useState(null);
  const [status, setStatus] = useState('loading');

  useEffect(() => {
    (async () => {
      try {
        const fx = (await tournamentApi.get(`/fixtures/${id}`)).data;
        const [t, teams] = await Promise.all([
          tournamentApi.get(`/tournaments/${fx.tournament_id}`),
          tournamentApi.get(`/tournaments/${fx.tournament_id}/teams`),
        ]);
        const map = Object.fromEntries(teams.data.map((x) => [x.id, x.name]));
        setCtx({
          fixtureId: id,
          tournamentId: fx.tournament_id,
          homeTeam: map[fx.home_team_id] || 'Home',
          awayTeam: map[fx.away_team_id] || 'Away',
          competition: t.data.name,
          venue: t.data.location || '',
          quickGoal: t.data.quick_goal || false,
          onComplete: () => nav(`/tournaments/${fx.tournament_id}`),
        });
        setStatus('ok');
      } catch (e) {
        setStatus(e?.response?.status === 401 ? 'auth' : 'error');
      }
    })();
  }, [id, nav]);

  if (status === 'auth') return <Navigate to="/login" replace />;
  if (status === 'loading') return <div className="min-h-screen bg-[#0A0A0A] text-gray-500 flex items-center justify-center font-sans">Loading fixture…</div>;
  if (status === 'error') return <div className="min-h-screen bg-[#0A0A0A] text-gray-500 flex items-center justify-center font-sans">Fixture not found.</div>;
  return <RefControl fixtureContext={ctx} />;
}

function RefereeTokenMatch() {
  const { token, fid } = useParams();
  const nav = useNavigate();
  const [ctx, setCtx] = useState(null);
  const [status, setStatus] = useState('loading');

  useEffect(() => {
    (async () => {
      try {
        const { data } = await tournamentApi.get(`/officials/by-token/${token}`);
        const f = (data.fixtures || []).find((x) => x.id === fid);
        if (!f) { setStatus('error'); return; }
        setCtx({
          fixtureId: fid,
          homeTeam: f.home,
          awayTeam: f.away,
          competition: data.competition,
          venue: data.venue,
          periods: f.periods,
          periodLength: f.period_length,
          quickGoal: data.quick_goal || false,
          submit: (payload) => tournamentApi.post(`/officials/${token}/fixtures/${fid}/result`, payload),
          onComplete: () => nav(`/checkin/${token}`),
        });
        setStatus('ok');
      } catch (e) {
        setStatus('error');
      }
    })();
  }, [token, fid, nav]);

  if (status === 'loading') return <div className="min-h-screen bg-[#0A0A0A] text-gray-500 flex items-center justify-center font-sans">Loading fixture…</div>;
  if (status === 'error') return <div className="min-h-screen bg-[#0A0A0A] text-gray-500 flex items-center justify-center font-sans">Fixture not found.</div>;
  return <RefControl fixtureContext={ctx} />;
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<RefControl />} />
          <Route path="/share/:id" element={<ShareReport />} />
          <Route path="/login" element={<Login />} />
          <Route path="/tournaments" element={<RequireAuth><Tournaments /></RequireAuth>} />
          <Route path="/tournaments/:id" element={<RequireAuth><TournamentDetail /></RequireAuth>} />
          <Route path="/referee/:id" element={<RequireAuth><RefereeMatch /></RequireAuth>} />
          <Route path="/live/:id" element={<PublicPortal />} />
          <Route path="/event/:eventId" element={<PublicEvent />} />
          <Route path="/checkin/:token" element={<Checkin />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/billing/return" element={<BillingReturn />} />
          <Route path="/ref/:token/:fid" element={<RefereeTokenMatch />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}
